/**
 * Admin Program Manager - CMS page for managing study abroad programs/courses
 * SEO landing pages for high-volume keywords like "MS in Computer Science USA"
 */
import { useState, useEffect } from 'react';
import { useAdminTheme } from '@/context/ThemeContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import PublishStatusCard from '@/components/admin/PublishStatusCard';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { toast } from 'sonner';
import { AdminLayout } from '@/components/admin/AdminLayout';
import {
  GraduationCap,
  Plus,
  Pencil,
  Trash2,
  Eye,
  EyeOff,
  Loader2,
  Search,
  ExternalLink,
  RefreshCw,
  Star,
  StarOff,
  Globe,
  DollarSign,
  Clock
} from 'lucide-react';

const API_URL = window.location.origin || '';

const DESTINATIONS = ['USA', 'UK', 'Canada', 'Australia', 'Germany', 'Multiple Countries'];
const DEGREE_LEVELS = ["Bachelor's", "Master's", "Doctorate", "Diploma"];
const FIELDS_OF_STUDY = [
  'Computer Science', 'Data Science', 'Artificial Intelligence', 'Business Administration',
  'Engineering', 'Medicine', 'Nursing', 'Business Analytics', 'Various', 'Various (Business, IT, Healthcare)'
];

const AdminProgramManager = () => {
  const { isDark } = useAdminTheme();
  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterDestination, setFilterDestination] = useState('all');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingProgram, setEditingProgram] = useState(null);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('basic');

  // Form state
  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    destination: 'USA',
    degree_level: "Master's",
    field_of_study: 'Computer Science',
    tagline: '',
    description: '',
    keywords: '',
    overview: '',
    duration: '',
    avg_tuition: '',
    intake_seasons: ['Fall'],
    eligibility: {
      education: '',
      minimum_gpa: '',
      english_tests: ['IELTS: 6.5+', 'TOEFL: 90+'],
      additional_tests: [],
      work_experience: ''
    },
    career_prospects: {
      job_roles: [],
      average_salary: '',
      top_employers: [],
      growth_outlook: ''
    },
    top_universities: [],
    why_choose: [],
    scholarships_info: '',
    visa_info: '',
    is_featured: false,
    is_active: true
  });

  // Fetch programs
  const fetchPrograms = async () => {
    try {
      const res = await fetch(`${API_URL}/api/programs/admin/all`);
      if (res.ok) {
        const data = await res.json();
        setPrograms(data);
      }
    } catch (error) {
      console.error('Error fetching programs:', error);
      toast.error('Failed to fetch programs');
    } finally {
      setLoading(false);
    }
  };

  // Seed initial data
  const seedPrograms = async () => {
    try {
      const res = await fetch(`${API_URL}/api/programs/admin/seed`, {
        method: 'POST'
      });
      const data = await res.json();
      toast.success(data.message);
      fetchPrograms();
    } catch (error) {
      console.error('Error seeding programs:', error);
      toast.error('Failed to seed programs');
    }
  };

  useEffect(() => {
    fetchPrograms();
  }, []);

  // Generate slug from title and destination
  const generateSlug = (title, destination) => {
    let slug = title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, '');
    if (destination && destination !== 'Multiple Countries') {
      slug += '-' + destination.toLowerCase().replace(/\s+/g, '-');
    }
    return slug;
  };

  // Handle form input change
  const handleInputChange = (field, value) => {
    setFormData(prev => {
      const newData = { ...prev, [field]: value };
      
      // Auto-generate slug when title or destination changes
      if (field === 'title' || field === 'destination') {
        const title = field === 'title' ? value : prev.title;
        const destination = field === 'destination' ? value : prev.destination;
        newData.slug = generateSlug(title, destination);
      }
      
      return newData;
    });
  };

  // Handle nested object changes
  const handleNestedChange = (parent, field, value) => {
    setFormData(prev => ({
      ...prev,
      [parent]: { ...prev[parent], [field]: value }
    }));
  };

  // Handle array fields (newline separated)
  const handleArrayChange = (field, value, isNested = false, parent = null) => {
    const items = value.split('\n').map(s => s.trim()).filter(s => s);
    if (isNested && parent) {
      handleNestedChange(parent, field, items);
    } else {
      handleInputChange(field, items);
    }
  };

  // Open dialog for new program
  const handleAddNew = () => {
    setEditingProgram(null);
    setFormData({
      title: '',
      slug: '',
      destination: 'USA',
      degree_level: "Master's",
      field_of_study: 'Computer Science',
      tagline: '',
      description: '',
      keywords: '',
      overview: '',
      duration: '',
      avg_tuition: '',
      intake_seasons: ['Fall'],
      eligibility: {
        education: '',
        minimum_gpa: '',
        english_tests: ['IELTS: 6.5+', 'TOEFL: 90+'],
        additional_tests: [],
        work_experience: ''
      },
      career_prospects: {
        job_roles: [],
        average_salary: '',
        top_employers: [],
        growth_outlook: ''
      },
      top_universities: [],
      why_choose: [],
      scholarships_info: '',
      visa_info: '',
      is_featured: false,
      is_active: true
    });
    setActiveTab('basic');
    setIsDialogOpen(true);
  };

  // Open dialog for editing
  const handleEdit = (program) => {
    setEditingProgram(program);
    setFormData({
      title: program.title || '',
      slug: program.slug || '',
      destination: program.destination || 'USA',
      degree_level: program.degree_level || "Master's",
      field_of_study: program.field_of_study || 'Computer Science',
      tagline: program.tagline || '',
      description: program.description || '',
      keywords: program.keywords || '',
      overview: program.overview || '',
      duration: program.duration || '',
      avg_tuition: program.avg_tuition || '',
      intake_seasons: program.intake_seasons || ['Fall'],
      eligibility: program.eligibility || {
        education: '',
        minimum_gpa: '',
        english_tests: [],
        additional_tests: [],
        work_experience: ''
      },
      career_prospects: program.career_prospects || {
        job_roles: [],
        average_salary: '',
        top_employers: [],
        growth_outlook: ''
      },
      top_universities: program.top_universities || [],
      why_choose: program.why_choose || [],
      scholarships_info: program.scholarships_info || '',
      visa_info: program.visa_info || '',
      is_featured: program.is_featured !== false,
      is_active: program.is_active !== false
    });
    setActiveTab('basic');
    setIsDialogOpen(true);
  };

  // Save program
  const handleSave = async () => {
    if (!formData.title || !formData.slug || !formData.destination) {
      toast.error('Title, Slug, and Destination are required');
      return;
    }

    setSaving(true);
    try {
      const url = editingProgram
        ? `${API_URL}/api/programs/admin/${editingProgram.id}`
        : `${API_URL}/api/programs/admin`;
      
      const method = editingProgram ? 'PUT' : 'POST';

      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      if (res.ok) {
        toast.success(editingProgram ? 'Program updated' : 'Program created');
        setIsDialogOpen(false);
        fetchPrograms();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to save program');
      }
    } catch (error) {
      console.error('Error saving program:', error);
      toast.error('Failed to save program');
    } finally {
      setSaving(false);
    }
  };

  // Toggle active status
  const handleToggleActive = async (program) => {
    try {
      const res = await fetch(`${API_URL}/api/programs/admin/${program.id}/toggle-active`, {
        method: 'POST'
      });
      if (res.ok) {
        toast.success(`Program ${program.is_active ? 'deactivated' : 'activated'}`);
        fetchPrograms();
      }
    } catch (error) {
      toast.error('Failed to toggle status');
    }
  };

  // Toggle featured status
  const handleToggleFeatured = async (program) => {
    try {
      const res = await fetch(`${API_URL}/api/programs/admin/${program.id}/toggle-featured`, {
        method: 'POST'
      });
      if (res.ok) {
        toast.success(`Program ${program.is_featured ? 'unfeatured' : 'featured'}`);
        fetchPrograms();
      }
    } catch (error) {
      toast.error('Failed to toggle featured');
    }
  };

  // Delete program
  const handleDelete = async (program) => {
    if (!window.confirm(`Are you sure you want to delete "${program.title}"?`)) {
      return;
    }

    try {
      const res = await fetch(`${API_URL}/api/programs/admin/${program.id}`, {
        method: 'DELETE'
      });
      if (res.ok) {
        toast.success('Program deleted');
        fetchPrograms();
      }
    } catch (error) {
      toast.error('Failed to delete program');
    }
  };

  // Filter programs
  const filteredPrograms = programs.filter(prog => {
    const matchesSearch = prog.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      prog.destination.toLowerCase().includes(searchTerm.toLowerCase()) ||
      prog.field_of_study.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesDestination = filterDestination === 'all' || prog.destination === filterDestination;
    return matchesSearch && matchesDestination;
  });

  const TabButton = ({ tab, label }) => (
    <button
      onClick={() => setActiveTab(tab)}
      className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
        activeTab === tab
          ? isDark ? 'bg-ux-cyan/20 text-ux-cyan' : 'bg-ux-blue/10 text-ux-blue'
          : isDark ? 'text-white/60 hover:text-white' : 'text-slate-600 hover:text-slate-900'
      }`}
    >
      {label}
    </button>
  );

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-program-manager">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>
            Program Manager
          </h1>
          <p className={`text-sm ${isDark ? 'text-white/60' : 'text-slate-500'}`}>
            Manage SEO landing pages for study abroad courses
          </p>
        </div>
        <div className="flex gap-2">
          {programs.length === 0 && (
            <Button variant="outline" onClick={seedPrograms} data-testid="seed-programs-btn">
              <RefreshCw className="w-4 h-4 mr-2" />
              Seed Initial Data
            </Button>
          )}
          <Button onClick={handleAddNew} className="bg-gradient-to-r from-ux-cyan to-ux-blue text-white" data-testid="add-program-btn">
            <Plus className="w-4 h-4 mr-2" />
            Add Program
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-4">
        <div className="relative flex-1 min-w-[200px]">
          <Search className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${isDark ? 'text-white/40' : 'text-slate-400'}`} />
          <Input
            placeholder="Search programs..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className={`pl-10 ${isDark ? 'bg-white/5 border-white/10' : ''}`}
            data-testid="search-programs"
          />
        </div>
        <Select value={filterDestination} onValueChange={setFilterDestination}>
          <SelectTrigger className={`w-[160px] ${isDark ? 'bg-white/5 border-white/10' : ''}`}>
            <SelectValue placeholder="Destination" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Destinations</SelectItem>
            {DESTINATIONS.map(d => (
              <SelectItem key={d} value={d}>{d}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Programs Table */}
      <Card className={isDark ? 'bg-white/5 border-white/10' : ''}>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin text-ux-blue" />
            </div>
          ) : filteredPrograms.length === 0 ? (
            <div className="text-center py-12">
              <GraduationCap className={`w-12 h-12 mx-auto mb-4 ${isDark ? 'text-white/20' : 'text-slate-300'}`} />
              <p className={isDark ? 'text-white/60' : 'text-slate-500'}>
                {searchTerm || filterDestination !== 'all' 
                  ? 'No programs found' 
                  : 'No programs yet. Click "Seed Initial Data" to add default SEO programs.'}
              </p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className={isDark ? 'border-white/10' : ''}>
                  <TableHead className={isDark ? 'text-white/70' : ''}>Program</TableHead>
                  <TableHead className={isDark ? 'text-white/70' : ''}>Destination</TableHead>
                  <TableHead className={isDark ? 'text-white/70' : ''}>Level</TableHead>
                  <TableHead className={isDark ? 'text-white/70' : ''}>Status</TableHead>
                  <TableHead className={`text-right ${isDark ? 'text-white/70' : ''}`}>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredPrograms.map((program) => (
                  <TableRow key={program.id} className={isDark ? 'border-white/10' : ''}>
                    <TableCell className={isDark ? 'text-white' : ''}>
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{program.title}</span>
                          {program.is_featured && (
                            <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
                          )}
                        </div>
                        <div className={`text-xs mt-1 ${isDark ? 'text-white/40' : 'text-slate-400'}`}>
                          /{program.slug}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className={isDark ? 'border-white/30 text-white/80' : ''}>
                        <Globe className="w-3 h-3 mr-1" />
                        {program.destination}
                      </Badge>
                    </TableCell>
                    <TableCell className={isDark ? 'text-white/80' : ''}>
                      {program.degree_level}
                    </TableCell>
                    <TableCell>
                      <Badge variant={program.is_active ? 'default' : 'secondary'}>
                        {program.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => window.open(`/programs/${program.slug}`, '_blank')}
                          title="Preview"
                        >
                          <ExternalLink className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleToggleFeatured(program)}
                          title={program.is_featured ? 'Unfeature' : 'Feature'}
                        >
                          {program.is_featured ? <StarOff className="w-4 h-4 text-yellow-500" /> : <Star className="w-4 h-4" />}
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleToggleActive(program)}
                          title={program.is_active ? 'Deactivate' : 'Activate'}
                        >
                          {program.is_active ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleEdit(program)}
                          title="Edit"
                        >
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDelete(program)}
                          className="text-red-500 hover:text-red-600"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Edit/Create Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className={`max-w-4xl max-h-[90vh] overflow-y-auto ${isDark ? 'bg-[#1a2830] border-white/10' : ''}`}>
          <DialogHeader>
            <DialogTitle className={`flex items-center gap-3 flex-wrap ${isDark ? 'text-white' : ''}`}>
              {editingProgram ? 'Edit Program' : 'Add New Program'}
              {editingProgram && (
                <Badge
                  variant={formData.is_active ? 'default' : 'secondary'}
                  className={formData.is_active
                    ? 'bg-emerald-500/15 text-emerald-600 border border-emerald-500/30 hover:bg-emerald-500/20'
                    : 'bg-amber-500/15 text-amber-600 border border-amber-500/30 hover:bg-amber-500/20'
                  }
                  data-testid="program-status-badge"
                >
                  {formData.is_active ? 'Published' : 'Draft'}
                </Badge>
              )}
            </DialogTitle>
            <DialogDescription className={isDark ? 'text-white/60' : ''}>
              {editingProgram ? 'Update SEO program details' : 'Create a new SEO landing page for a study abroad program'}
            </DialogDescription>
          </DialogHeader>

          {/* Tabs */}
          <div className="flex gap-2 border-b pb-2 mb-4 overflow-x-auto">
            <TabButton tab="basic" label="Basic Info" />
            <TabButton tab="seo" label="SEO Content" />
            <TabButton tab="eligibility" label="Eligibility" />
            <TabButton tab="career" label="Career" />
            <TabButton tab="universities" label="Universities" />
          </div>

          <div className="space-y-6 py-4">
            {/* Basic Info Tab */}
            {activeTab === 'basic' && (
              <>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className={isDark ? 'text-white' : ''}>Program Title *</Label>
                    <Input
                      value={formData.title}
                      onChange={(e) => handleInputChange('title', e.target.value)}
                      placeholder="e.g., MS in Computer Science"
                      className={isDark ? 'bg-white/5 border-white/10' : ''}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className={isDark ? 'text-white' : ''}>Destination *</Label>
                    <Select value={formData.destination} onValueChange={(v) => handleInputChange('destination', v)}>
                      <SelectTrigger className={isDark ? 'bg-white/5 border-white/10' : ''}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {DESTINATIONS.map(d => (
                          <SelectItem key={d} value={d}>{d}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className={isDark ? 'text-white' : ''}>Degree Level</Label>
                    <Select value={formData.degree_level} onValueChange={(v) => handleInputChange('degree_level', v)}>
                      <SelectTrigger className={isDark ? 'bg-white/5 border-white/10' : ''}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {DEGREE_LEVELS.map(d => (
                          <SelectItem key={d} value={d}>{d}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className={isDark ? 'text-white' : ''}>Field of Study</Label>
                    <Select value={formData.field_of_study} onValueChange={(v) => handleInputChange('field_of_study', v)}>
                      <SelectTrigger className={isDark ? 'bg-white/5 border-white/10' : ''}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {FIELDS_OF_STUDY.map(f => (
                          <SelectItem key={f} value={f}>{f}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className={isDark ? 'text-white' : ''}>URL Slug *</Label>
                  <Input
                    value={formData.slug}
                    onChange={(e) => handleInputChange('slug', e.target.value)}
                    placeholder="e.g., ms-in-computer-science-usa"
                    className={isDark ? 'bg-white/5 border-white/10' : ''}
                  />
                  <p className={`text-xs ${isDark ? 'text-white/40' : 'text-slate-400'}`}>
                    URL: /programs/{formData.slug || 'slug'}
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className={isDark ? 'text-white' : ''}>Duration</Label>
                    <Input
                      value={formData.duration}
                      onChange={(e) => handleInputChange('duration', e.target.value)}
                      placeholder="e.g., 1.5 - 2 years"
                      className={isDark ? 'bg-white/5 border-white/10' : ''}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className={isDark ? 'text-white' : ''}>Average Tuition</Label>
                    <Input
                      value={formData.avg_tuition}
                      onChange={(e) => handleInputChange('avg_tuition', e.target.value)}
                      placeholder="e.g., $40,000 - $70,000 per year"
                      className={isDark ? 'bg-white/5 border-white/10' : ''}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className={isDark ? 'text-white' : ''}>Intake Seasons (one per line)</Label>
                  <Textarea
                    value={(formData.intake_seasons || []).join('\n')}
                    onChange={(e) => handleArrayChange('intake_seasons', e.target.value)}
                    placeholder="Fall&#10;Spring"
                    rows={2}
                    className={isDark ? 'bg-white/5 border-white/10' : ''}
                  />
                </div>

                <PublishStatusCard
                  isPublished={formData.is_active}
                  onChange={(checked) => handleInputChange('is_active', checked)}
                  isDark={isDark}
                  entityLabel="program"
                  publicRoute="/programs"
                  testId="program-publish-toggle"
                />

                <div className="flex items-center gap-4">
                  <Switch
                    checked={formData.is_featured}
                    onCheckedChange={(checked) => handleInputChange('is_featured', checked)}
                  />
                  <Label className={isDark ? 'text-white' : ''}>Featured Program</Label>
                </div>
              </>
            )}

            {/* SEO Content Tab */}
            {activeTab === 'seo' && (
              <>
                <div className="space-y-2">
                  <Label className={isDark ? 'text-white' : ''}>Tagline (Hero Subtitle)</Label>
                  <Input
                    value={formData.tagline}
                    onChange={(e) => handleInputChange('tagline', e.target.value)}
                    placeholder="e.g., Top-Ranked CS Programs at MIT, Stanford, CMU with $150K+ Salaries"
                    className={isDark ? 'bg-white/5 border-white/10' : ''}
                  />
                </div>

                <div className="space-y-2">
                  <Label className={isDark ? 'text-white' : ''}>Meta Description (SEO)</Label>
                  <Textarea
                    value={formData.description}
                    onChange={(e) => handleInputChange('description', e.target.value)}
                    placeholder="150-160 chars description for search engines..."
                    rows={2}
                    className={isDark ? 'bg-white/5 border-white/10' : ''}
                  />
                  <p className={`text-xs ${isDark ? 'text-white/40' : 'text-slate-400'}`}>
                    {formData.description.length}/160 characters
                  </p>
                </div>

                <div className="space-y-2">
                  <Label className={isDark ? 'text-white' : ''}>SEO Keywords</Label>
                  <Textarea
                    value={formData.keywords}
                    onChange={(e) => handleInputChange('keywords', e.target.value)}
                    placeholder="MS in Computer Science USA, MS CS USA fees, computer science masters USA..."
                    rows={3}
                    className={isDark ? 'bg-white/5 border-white/10' : ''}
                  />
                </div>

                <div className="space-y-2">
                  <Label className={isDark ? 'text-white' : ''}>Overview Content</Label>
                  <Textarea
                    value={formData.overview}
                    onChange={(e) => handleInputChange('overview', e.target.value)}
                    placeholder="Main content about the program. Use double newlines for paragraphs."
                    rows={8}
                    className={isDark ? 'bg-white/5 border-white/10' : ''}
                  />
                </div>

                <div className="space-y-2">
                  <Label className={isDark ? 'text-white' : ''}>Why Choose This Program (one per line)</Label>
                  <Textarea
                    value={(formData.why_choose || []).join('\n')}
                    onChange={(e) => handleArrayChange('why_choose', e.target.value)}
                    placeholder="Highest ROI: ₹33-50L investment → ₹1-1.5 Cr salary&#10;3-year STEM OPT work authorization"
                    rows={5}
                    className={isDark ? 'bg-white/5 border-white/10' : ''}
                  />
                </div>

                <div className="space-y-2">
                  <Label className={isDark ? 'text-white' : ''}>Scholarships Info</Label>
                  <Textarea
                    value={formData.scholarships_info}
                    onChange={(e) => handleInputChange('scholarships_info', e.target.value)}
                    placeholder="Scholarship opportunities and funding options..."
                    rows={3}
                    className={isDark ? 'bg-white/5 border-white/10' : ''}
                  />
                </div>

                <div className="space-y-2">
                  <Label className={isDark ? 'text-white' : ''}>Visa Info</Label>
                  <Textarea
                    value={formData.visa_info}
                    onChange={(e) => handleInputChange('visa_info', e.target.value)}
                    placeholder="Visa requirements and post-study work options..."
                    rows={3}
                    className={isDark ? 'bg-white/5 border-white/10' : ''}
                  />
                </div>
              </>
            )}

            {/* Eligibility Tab */}
            {activeTab === 'eligibility' && (
              <>
                <div className="space-y-2">
                  <Label className={isDark ? 'text-white' : ''}>Educational Background</Label>
                  <Input
                    value={formData.eligibility?.education || ''}
                    onChange={(e) => handleNestedChange('eligibility', 'education', e.target.value)}
                    placeholder="e.g., B.Tech/BE in CS, IT, ECE or related field"
                    className={isDark ? 'bg-white/5 border-white/10' : ''}
                  />
                </div>

                <div className="space-y-2">
                  <Label className={isDark ? 'text-white' : ''}>Minimum GPA</Label>
                  <Input
                    value={formData.eligibility?.minimum_gpa || ''}
                    onChange={(e) => handleNestedChange('eligibility', 'minimum_gpa', e.target.value)}
                    placeholder="e.g., 3.0/4.0 or 7.5+ CGPA"
                    className={isDark ? 'bg-white/5 border-white/10' : ''}
                  />
                </div>

                <div className="space-y-2">
                  <Label className={isDark ? 'text-white' : ''}>English Tests (one per line)</Label>
                  <Textarea
                    value={(formData.eligibility?.english_tests || []).join('\n')}
                    onChange={(e) => handleArrayChange('english_tests', e.target.value, true, 'eligibility')}
                    placeholder="IELTS: 7.0+ overall&#10;TOEFL: 100+"
                    rows={3}
                    className={isDark ? 'bg-white/5 border-white/10' : ''}
                  />
                </div>

                <div className="space-y-2">
                  <Label className={isDark ? 'text-white' : ''}>Additional Tests (one per line)</Label>
                  <Textarea
                    value={(formData.eligibility?.additional_tests || []).join('\n')}
                    onChange={(e) => handleArrayChange('additional_tests', e.target.value, true, 'eligibility')}
                    placeholder="GRE: 320+ (Quant: 165+)&#10;Some universities are GRE-optional"
                    rows={3}
                    className={isDark ? 'bg-white/5 border-white/10' : ''}
                  />
                </div>

                <div className="space-y-2">
                  <Label className={isDark ? 'text-white' : ''}>Work Experience</Label>
                  <Input
                    value={formData.eligibility?.work_experience || ''}
                    onChange={(e) => handleNestedChange('eligibility', 'work_experience', e.target.value)}
                    placeholder="e.g., Not required, but 1-2 years preferred"
                    className={isDark ? 'bg-white/5 border-white/10' : ''}
                  />
                </div>
              </>
            )}

            {/* Career Tab */}
            {activeTab === 'career' && (
              <>
                <div className="space-y-2">
                  <Label className={isDark ? 'text-white' : ''}>Job Roles (one per line)</Label>
                  <Textarea
                    value={(formData.career_prospects?.job_roles || []).join('\n')}
                    onChange={(e) => handleArrayChange('job_roles', e.target.value, true, 'career_prospects')}
                    placeholder="Software Development Engineer&#10;Data Scientist&#10;ML Engineer"
                    rows={5}
                    className={isDark ? 'bg-white/5 border-white/10' : ''}
                  />
                </div>

                <div className="space-y-2">
                  <Label className={isDark ? 'text-white' : ''}>Average Salary</Label>
                  <Input
                    value={formData.career_prospects?.average_salary || ''}
                    onChange={(e) => handleNestedChange('career_prospects', 'average_salary', e.target.value)}
                    placeholder="e.g., $120,000 - $180,000/year (₹1 - 1.5 Cr)"
                    className={isDark ? 'bg-white/5 border-white/10' : ''}
                  />
                </div>

                <div className="space-y-2">
                  <Label className={isDark ? 'text-white' : ''}>Top Employers (one per line)</Label>
                  <Textarea
                    value={(formData.career_prospects?.top_employers || []).join('\n')}
                    onChange={(e) => handleArrayChange('top_employers', e.target.value, true, 'career_prospects')}
                    placeholder="Google&#10;Microsoft&#10;Amazon"
                    rows={4}
                    className={isDark ? 'bg-white/5 border-white/10' : ''}
                  />
                </div>

                <div className="space-y-2">
                  <Label className={isDark ? 'text-white' : ''}>Growth Outlook</Label>
                  <Input
                    value={formData.career_prospects?.growth_outlook || ''}
                    onChange={(e) => handleNestedChange('career_prospects', 'growth_outlook', e.target.value)}
                    placeholder="e.g., 22% job growth by 2030"
                    className={isDark ? 'bg-white/5 border-white/10' : ''}
                  />
                </div>
              </>
            )}

            {/* Universities Tab */}
            {activeTab === 'universities' && (
              <div className="space-y-2">
                <Label className={isDark ? 'text-white' : ''}>Top Universities (one per line)</Label>
                <Textarea
                  value={(formData.top_universities || []).join('\n')}
                  onChange={(e) => handleArrayChange('top_universities', e.target.value)}
                  placeholder="MIT&#10;Stanford University&#10;Carnegie Mellon University&#10;UC Berkeley"
                  rows={10}
                  className={isDark ? 'bg-white/5 border-white/10' : ''}
                />
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving} className="bg-gradient-to-r from-ux-cyan to-ux-blue text-white">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              {editingProgram ? 'Update Program' : 'Create Program'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      </div>
    </AdminLayout>
  );
};

export default AdminProgramManager;
